import { UIViewSpec, UIViewAddSpec, UIViewAddItem } from './view';
import { UIEventSubscription, UIMediaQuery } from './types';
import { uiAnimate, UIAnimationName } from './animate';
import { uiTrackAttach } from './attach';
import { ui_query_to_text } from './media';

export function getAddNode(item: UIViewAddItem): Node {
    if(typeof item === 'string')
        return document.createTextNode(item);
    else
        return item.ui_elem;
}

// TODO: replace with DOMRect
interface MyClientRect {
    left: number;
    top: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
}

declare global {
    interface Node {
        ui_is_helper?: boolean;
        ui_elem: this;
    }

    interface Element {
        cloneNode(deep?: boolean): this;

        ui_anchor_name?: string;
        ui_drop_position: MyClientRect;
        ui_drop_text_only: boolean;
        ui_anim_token?: {};
        ui_hidden?: boolean;
        ui_visible_proxy?: UIViewSpec;
        ui_scroll_id?: string;
        ui_viewRect(): DOMRect;
        ui_docRect(): MyClientRect;
        ui_docTextRect(): MyClientRect;
        ui_add(content: UIViewAddSpec): Node | null;
        ui_insert(content: UIViewAddSpec, ref: UIViewSpec | Node | null | undefined, after?: boolean): Node | null;
        ui_prepend(content: UIViewAddSpec): Node | null;
        ui_replace(content: UIViewAddSpec): void;
        ui_render(content: UIViewAddSpec): void;
        ui_empty(new_content?: UIViewAddSpec): void;
        ui_show(animation?: UIAnimationName, show?: boolean): void;
        ui_hide(animation?: UIAnimationName): void;
        ui_setUniqueID(): string;
        ui_setQueryStyle(query: UIMediaQuery, style: string, value: string): void;
        ui_on<K extends keyof HTMLElementEventMap>(event: K, cb: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any): UIEventSubscription;
    }
}

Object.defineProperty(Node.prototype, 'ui_elem', {
    configurable: true,
    enumerable: true,
    get: function(this: Node) {
        return this;
    }
});

Element.prototype.ui_viewRect = function(this: Element): DOMRect {
    return this.getBoundingClientRect();
}

function ui_add_helper(where: Element, arr: (false | undefined | UIViewAddSpec)[]): Node | null {
    let ret: Node | null = null;

    for(const c of arr) {
        if(c) {
            if(Array.isArray(c)) {
                ret = ui_add_helper(where, c)
            } else {
                ret = where.appendChild(getAddNode(c));
            }
        }
    }

    return ret;
}

Element.prototype.ui_add = function(this: Element, content: UIViewAddSpec) {
    uiTrackAttach();
    return Array.isArray(content) ? ui_add_helper(this, content) : this.appendChild(getAddNode(content));
}

function ui_insert_helper(where: Element, ref: Node | null, arr: (false | undefined | UIViewAddSpec)[]): Node | null {
    let ret: Node | null = null;

    for(const c of arr) {
        if(c) {
            if(Array.isArray(c)) {
                ret = ui_insert_helper(where, ref, c)
            } else {
                ret = where.insertBefore(getAddNode(c), ref);
            }
        }
    }

    return ret;
}

Element.prototype.ui_insert = function(this: Element, content: UIViewAddSpec, ref: UIViewSpec | Node | null | undefined, after?: boolean) {
    if(!ref) return after ? this.ui_insert(content, this.firstChild) : this.ui_add(content);

    ref = ref.ui_elem;

    if(after)
        ref = ref.nextSibling;

    uiTrackAttach();
    return Array.isArray(content) ? ui_insert_helper(this, ref, content) : this.insertBefore(getAddNode(content), ref);
}

Element.prototype.ui_prepend = function(this: Element, content: UIViewAddSpec) {
    return this.ui_insert(content, this.firstChild);
}

Element.prototype.ui_replace = function(this: Element, content: UIViewAddSpec): void {
    const parent = this.parentElement;
    if(parent) {
        parent.ui_insert(content, this);
        parent.removeChild(this);
    }
}

Element.prototype.ui_empty = function(this: Element, new_content?: UIViewAddSpec): void {
    uiTrackAttach();

    for(const c of Array.from(this.childNodes)) {
        if(!c.ui_is_helper)
            c.remove();
    }

    if(new_content)
        this.ui_add(new_content);
}

Element.prototype.ui_render = function(this: Element, new_content: UIViewAddItem[]): void {
    uiTrackAttach();

    const children = Array.from(this.childNodes);
    const content = new_content.map(x => getAddNode(x));
    const added = new Set<Node>();

    for(let i = 0; i < children.length; i++) {
        const c = children[i];
        if(c.ui_is_helper) continue;

        const f = content.findIndex(x => x.ui_elem === c);
        if(f === -1) {
            if(!added.has(c))
                c.remove();
        } else {
            for(let j = 0; j < f; j++) {
                added.add(content[j]);
                this.insertBefore(content[j], c);
            }
            content.splice(0, f + 1);
        }
    }

    this.append(...content);
}

Element.prototype.ui_show = function(this: HTMLElement, animation?: UIAnimationName, show?: boolean) {
    if(show === false) {
        this.ui_hide(animation);
    } else if(this.ui_visible_proxy) {
        this.ui_visible_proxy.ui_show(animation);
    } else if(this.ui_hidden) {
        this.ui_hidden = false;
        this.style.display = '';
        if(animation)
            uiAnimate([{ what: this, animation: animation, duration: 0.3 }])
    }
}

Element.prototype.ui_hide = function(this: HTMLElement, animation?: UIAnimationName) {
    if(this.ui_visible_proxy) {
        this.ui_visible_proxy.ui_hide(animation);
    } else if(!this.ui_hidden) {
        this.ui_hidden = true;
        if(animation) {
            uiAnimate([{ what: this, animation: animation, duration: 0.3, reverse: true }], () => {
                this.style.display = 'none';
            });
        } else {
            this.style.display = 'none';
        }
    }
}


Element.prototype.ui_docRect = function(this: HTMLElement): MyClientRect {
    const rect = this.getBoundingClientRect();

    return {
        left: rect.left + window.scrollX,
        top: rect.top + window.scrollY,
        right: rect.right + window.scrollX,
        bottom: rect.bottom + window.scrollY,
        width: rect.width,
        height: rect.height
    };
}

Element.prototype.ui_docTextRect = function(this: HTMLElement): MyClientRect {
    const range = document.createRange();
    range.selectNodeContents(this);
    const rect = range.getBoundingClientRect();

    return {
        left: rect.left + window.scrollX,
        top: rect.top + window.scrollY,
        right: rect.right + window.scrollX,
        bottom: rect.bottom + window.scrollY,
        width: rect.width,
        height: rect.height
    };
}

let unique_id_counter = 1;

Element.prototype.ui_setUniqueID = function(this: HTMLElement): string {
    if(!this.id) {
        this.id = 'y-u-' + (unique_id_counter)++;
    }
    return this.id;
}

Element.prototype.ui_setQueryStyle = function(query: UIMediaQuery, style: string, value: string) {
    const elem = document.createElement('style');
    const id = this.ui_setUniqueID();
    elem.innerHTML = '@media ' + ui_query_to_text[query] + '{#' + id + '{' + style + ':' + value + ';' + '}}';
    document.head.appendChild(elem);
}

Element.prototype.ui_on = function <K extends keyof HTMLElementEventMap>(event: K, cb: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any) {
    (this as HTMLElement).addEventListener(event, cb);
    return { event: event, elem: this as HTMLElement, func: cb as any };
}

export function off(s: UIEventSubscription) {
    s.elem.removeEventListener(s.event, s.func);
}
